import queryString from 'query-string';
import pick from 'lodash/pick';
import omit from 'lodash/omit';

import {apiClient, FetchOptions} from 'common/util/api-client';

import {ExpirableCache, MAX_EXPIRY_SECONDS} from './expirable-cache';

export interface QueryOptions {
  /**
   * Force use network and clear response cache.
   * response cache stores processed responses per browser session.
   */
  clearCache?: boolean;
  /**
   * Query from fetch-cache, don't use network.
   * Note: query() will return null promise if match fails. User needs to fallback to network promise.
   */
  fromFetchCache?: boolean;
  /**
   * When the network response comes back, update fetch-cache so it can be queried next time with fromFetchCache.
   */
  updateFetchCache?: boolean;

  method?: `GET` | `POST` | `PUT` | `PATCH` | `DELETE`;

  contentType?: string;

  abortSignal?: AbortSignal;

  /**
   * HTTP headers to send with the request.
   */
  headers?: Record<string, string>;
}

/**
 * @deprecated
 *
 * IMPORTANT: extending BaseClient is a deprecated pattern.
 * Use apiClient.get|post|put|patch|delete from an api helper module.
 * It makes code logic easier to understand and simpler.
 * apiClient supports caching via fromFetchCache and updateFetchCache options
 * for memory caching, use a store instead which supports watching when the cache gets updated so ui can be redrawn
 */
export abstract class BaseClient<ResponseType = any> {
  public responseCache: ExpirableCache;

  constructor({cacheTtl = MAX_EXPIRY_SECONDS} = {}) {
    this.responseCache = new ExpirableCache(cacheTtl);
  }

  // region To be implemented by inheriting class

  public buildEndpoint(_params = {}): string {
    throw new Error(`Must be implemented`);
  }

  public buildRequestBody(_params = {}) {
    return null;
  }

  public buildQueryParams(_params = {}) {
    return null;
  }

  public processResponse(response, _params?: any): ResponseType {
    return response;
  }

  // endregion

  public getCachedResponse(params) {
    return this.responseCache.get(params);
  }

  public getUrl(params) {
    let url = `/${this.buildEndpoint(params)}`;
    const queryParams = this.buildQueryParams(params);
    if (queryParams) {
      url += `?${queryString.stringify(queryParams)}`;
    }

    return url;
  }

  public getFetchOptions(params, queryOpts: QueryOptions): FetchOptions {
    const fetchOptions: FetchOptions = pick(queryOpts, [
      `method`,
      `headers`,
      `abortSignal`,
      `fromFetchCache`,
      `updateFetchCache`,
    ]);

    const body = this.buildRequestBody(params);

    if (!fetchOptions.method) {
      fetchOptions.method = body ? `POST` : `GET`;
    }

    if (queryOpts.contentType) {
      fetchOptions.headers = {
        'Content-Type': queryOpts.contentType,
      };
    }

    if (body) {
      fetchOptions.body = body;
    }

    return fetchOptions;
  }

  public async query(params: {[key: string]: any}, queryOpts: QueryOptions = {}): Promise<ResponseType> {
    const filteredParams = omit(params, [`useQueryCache`]);
    const filteredQueryOpts = omit(queryOpts, [`clearCache`]);
    const cacheParams = {...filteredParams, ...filteredQueryOpts};

    const fetchOptions: FetchOptions = this.getFetchOptions(params, queryOpts);
    if (!queryOpts.fromFetchCache && fetchOptions.method === `GET`) {
      if (queryOpts.clearCache) {
        this.responseCache.clear(cacheParams);
      } else {
        if (this.responseCache.has(cacheParams)) {
          return this.responseCache.get(cacheParams);
        }
      }
    }

    const url = this.getUrl(params);

    const dataType = params.format === `csv` ? `text` : `json`;

    // eslint-disable-next-line local-rules/deprecated-member-access
    return apiClient
      .fetch(url, fetchOptions)
      .then((response) => {
        return response && response.status !== 204 && response[dataType](); // 204 is No content
      })
      .then((response) => {
        // fetch cache miss will be returned as null
        if (response === null) {
          return response;
        }
        const processed = this.processResponse(response, params);
        if (fetchOptions.method === `GET`) {
          this.responseCache.set(cacheParams, processed);
        }
        return processed;
      });
  }
}
